import clsx from 'clsx';
import React from 'react';

import { SUGGESTED_PROMPT_QUERY_PARAM } from '@components/pages/AI/DeepResearch/constants';
import useEventTracking from '@hooks/useEventTracking';
import { deep_research_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import SuggestedAiPromptIcon from './SuggestedAiPromptIcon';
import { SuggestedAiPromptCardAs } from './constants';
import { SuggestedAiPromptCardProps } from './interfaces';

const transformTextWithStrongFirstWord = (text: string): string => {
  const words = text.trim().split(' ');
  if (words.length === 0) return text;

  const firstWord = words[0];
  const remainingText = words.slice(1).join(' ');

  return `<strong class="transition-colors group-hover/prompt:text-primary-magenta">${firstWord}</strong> ${remainingText}`;
};

const SuggestedAiPromptCard = ({
  as = SuggestedAiPromptCardAs.Button,
  className,
  disablePrompts,
  handleSuggestionClick,
  suggestedAiPrompt,
}: SuggestedAiPromptCardProps) => {
  const { trackEvent } = useEventTracking();
  const { prompt, iconSvg } = suggestedAiPrompt;
  const Link = as;

  const parsedText = AuthoredContentReactToHtmlParser(transformTextWithStrongFirstWord(prompt));

  const handlePromptClick = () => {
    if (disablePrompts) return;

    handleSuggestionClick(prompt);
  };

  return (
    <Link
      aria-label="Autopopulate this prompt into the textarea field"
      className={clsx(
        className,
        'group/prompt relative flex flex-col gap-24 border-2 border-solid border-grayscale-beige bg-white p-24 text-left !no-underline transition-colors',
        {
          'pointer-events-none cursor-not-allowed': disablePrompts,
          'cursor-pointer hover:border-primary-magenta': !disablePrompts,
        }
      )}
      disabled={disablePrompts}
      {...(as === SuggestedAiPromptCardAs.Anchor
        ? { href: deep_research_path({ [SUGGESTED_PROMPT_QUERY_PARAM]: prompt }) }
        : { onClick: handlePromptClick })}
      onClickCapture={() => {
        trackEvent(click({ clickTarget: ClickTarget.SuggestedPrompt, clickText: prompt }));
      }}
    >
      <SuggestedAiPromptIcon disablePrompts={disablePrompts} promptUrl={iconSvg} />
      <span
        className={clsx(
          'inline-block font-suisse-screen text-byline-sm font-normal sm:text-caption-md',
          disablePrompts
            ? 'text-grayscale-gray *:group-hover/prompt:!text-grayscale-gray'
            : 'text-grayscale-charcoal'
        )}
      >
        {parsedText}
      </span>
    </Link>
  );
};

export default SuggestedAiPromptCard;
